export type GroupImportRow = { email: string; group: string; line: number };
export type GroupImportFailure = {
  row: GroupImportRow;
  reason: 'unknown-email' | 'unknown-group' | 'already-in-group';
};
export type GroupMembershipOperation = {
  groupId: string;
  userId: string;
  action: 'add' | 'remove';
};

export function computeGroupMembershipDiff(
  current: Map<string, Set<string>>,
  usersByEmail: Map<string, string>,
  groupsByName: Map<string, string>,
  rows: GroupImportRow[],
  mode: 'add-only' | 'replace'
): { operations: GroupMembershipOperation[]; invalid: GroupImportFailure[] } {
  const target = new Map<string, Set<string>>();
  const invalid: GroupImportFailure[] = [];
  // Remember the row behind each intended add, so a membership conflict can be
  // reported against the CSV line the operator has to fix.
  const rowFor = new Map<string, GroupImportRow>();
  for (const row of rows) {
    const userId = usersByEmail.get(row.email.trim().toLowerCase());
    if (!userId) {
      invalid.push({ row, reason: 'unknown-email' });
      continue;
    }
    const groupId = groupsByName.get(row.group.trim());
    if (!row.group.trim()) continue;
    if (!groupId) {
      invalid.push({ row, reason: 'unknown-group' });
      continue;
    }
    const members = target.get(groupId) ?? new Set<string>();
    members.add(userId);
    target.set(groupId, members);
    rowFor.set(`${groupId}:${userId}`, row);
  }

  const removals: GroupMembershipOperation[] = [];
  const additions: GroupMembershipOperation[] = [];
  for (const [groupId, desired] of target) {
    const existing = current.get(groupId) ?? new Set<string>();
    for (const userId of desired)
      if (!existing.has(userId)) additions.push({ groupId, userId, action: 'add' });
    if (mode === 'replace')
      for (const userId of existing)
        if (!desired.has(userId)) removals.push({ groupId, userId, action: 'remove' });
  }

  // A member belongs to at most one group, so an add whose subject is still in
  // some OTHER group is rejected by the server. Catch it in the preview rather
  // than partway through applying: the operator can add the missing removal to
  // the CSV instead of discovering a half-applied import.
  const freed = new Set(removals.map((operation) => `${operation.groupId}:${operation.userId}`));
  const plannedGroupByUser = new Map<string, string>();
  for (const [groupId, members] of current)
    for (const userId of members)
      if (!freed.has(`${groupId}:${userId}`)) plannedGroupByUser.set(userId, groupId);
  const keptAdditions: GroupMembershipOperation[] = [];
  for (const addition of additions) {
    const blockingGroupId = plannedGroupByUser.get(addition.userId);
    const row = rowFor.get(`${addition.groupId}:${addition.userId}`);
    if (blockingGroupId !== undefined && blockingGroupId !== addition.groupId && row)
      invalid.push({ row, reason: 'already-in-group' });
    else {
      keptAdditions.push(addition);
      plannedGroupByUser.set(addition.userId, addition.groupId);
    }
  }

  // Removals first: a move is only legal once the old membership is gone, and
  // the two halves can land in different groups' iterations.
  return { operations: [...removals, ...keptAdditions], invalid };
}
